import { splitCamelCase, isObjectArray, isStringArray } from './common';

/**
 * @function parseAuditData takes 3 parameters
 * It converts @param {*} obj into array and push value to temp array
 *
 *
 * @param {*} obj
 * @param {string} eventType
 * @param {string} keyPath
 *
 * @param keyPath is default root
 * for object  we add '.' in keyPath and for array we add * in keyPath
 * some examples root.userRoles, root.userRoles.*.role.rolePermissions
 *
 */

export function parseAuditData(obj, eventType, keyPath = 'root') {
  let tempArr = [];
  if (typeof obj !== 'object') {
    return tempArr;
  }
  for (let [key, value] of Object.entries(obj)) {
    // a val variable is defined to compute the value
    let val;

    // if key is rolePermissions and val like  value[0].permission.permissionType, join the array
    if (
      key === 'rolePermissions' &&
      isObjectArray(value) &&
      value[0]?.permission.permissionType
    ) {
      value = value.map(item => item.permission.permissionType).join(', ');
    }

    // uses function getObjectValue for more documentation go to define
    val = getObjectValue(value, eventType, keyPath + '.' + key);

    // check data to ignore based on eventType from audit trail
    // check auditTrailMap for better understanding
    const ignore = auditTrailMap[eventType]?.ignored?.findIndex(
      ignoreId => ignoreId === keyPath + '.' + key
    );
    // check which to ignore
    if (ignore && ignore === -1) {
      // check data to resolve based on eventType from audit trail
      // check auditTrailMap for better understanding
      const resolve = auditTrailMap[eventType]?.resolve?.findIndex(
        resolveId => resolveId === keyPath + '.' + key
      );

      const resolveEpoch = auditTrailMap[eventType]?.resolve_epoch?.findIndex(
        resolveId => resolveId === keyPath + '.' + key
      );

      // checks which to resolve
      if (typeof resolve !== 'undefined' && resolve !== -1) {
        tempArr.push({
          id: key,
          val: val + '_resolve',
          title: splitCamelCase(key)
        });

        // else push
      } else if (typeof resolveEpoch !== 'undefined' && resolveEpoch !== -1) {
        tempArr.push({
          id: key,
          val: val + '_resolveEpoch',
          title: splitCamelCase(key)
        });
      } else {
        tempArr.push({ id: key, val, title: splitCamelCase(key) });
      }
    }
  }
  return tempArr;
}

/**
 *
 * @param {*} val
 * @param {string} eventType
 * @param {string} keyPath
 */
export function getObjectValue(val, eventType, keyPath) {
  if (typeof val !== 'object') {
    return val;
  }
  if (isStringArray(val)) {
    return val.join(', ');
  }
  if (isObjectArray(val)) {
    const temap = [];
    val.forEach(itemVal => {
      const itemParsed = parseAuditData(itemVal, eventType, keyPath + '.*');
      temap.push(itemParsed);
    });

    return temap;
  }

  const temparr = parseAuditData(val, eventType, keyPath);

  return temparr;
}

// Audit trail Map > AuditTrailHelper.js
// contain propoerty to ignore or resolve a particular val
// based on id.The path is entered in in ignored array and
// resolve array

export const auditTrailMap = {
  // Search Service
  archive_viewed: { ignored: [], resolve_date: [], noPreview: true },
  discovery_archive_viewed: { ignored: [], noPreview: true },
  archive_downloaded: { ignored: [] },
  attachment_download: { noPreview: true },
  redeliver: { ignored: [], noPreview: true },
  mark_as_spam: {
    ignored: [],
    noPreview: true
  },
  mark_not_spam: {
    ignored: [],
    noPreview: true
  },

  // Archive Explorer
  vault_archive_viewed: { ignored: [], noPreview: true },
  vault_discovery_archive_viewed: {
    ignored: [],
    noPreview: true
  },
  vault_archive_downloaded: { ignored: [], noPreview: true },

  get_project: {
    ignored: ['root.id', 'root.dbVersion'],
    resolve: [
      'root.projectOwner',
      'root.collaborators.*.primaryKey.userId',
      'root.projectSearchUsers.*.primaryKey.userId'
    ]
  },
  widget_update: {
    ignored: []
  },
  annotation_add: {
    ignored: []
  },
  annotation_delete: {
    ignored: []
  },

  // Discovery Project

  get_archives: {
    ignored: []
  },
  remove_archives: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.startDate',
      'root.creationDate',
      'root.lastUpdated',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',
      'root.approvers.*.primaryKey.userId',
      'root.collaborators.*.primaryKey.userId',
      'root.approverComments.*.approverUserId',
      'root.projectSearchUsers.*.primaryKey.userId'
    ]
  },
  add_collaborators: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.creationDate',
      'root.lastUpdated',
      'root.endDate',
      'root.startDate',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',
      'root.projectSearchUsers.*.primaryKey.userId',
      'root.approverComments.*.approverUserId',
      'root.approverComments.*.UserId',
      'root.collaborators.*.primaryKey.userId',
      'root.approvers.*.primaryKey.userId'
    ]
  },
  remove_collaborators: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.creationDate',
      'root.lastUpdated',
      'root.endDate',
      'root.startDate',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',
      'root.projectSearchUsers.*.primaryKey.userId',
      'root.approverComments.*.approverUserId',
      'root.collaborators.*.primaryKey.userId',
      'root.approvers.*.primaryKey.userId'
    ]
  },
  archive_project: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.startDate',
      'root.creationDate',
      'root.lastUpdated',
      'root.endDate',
      'root.approvers.primaryKey.*.decisionDate',
      'root.approverComments.*.date'
    ],
    resolve: [
      'root.projectOwner',
      'root.approvers.*.primaryKey.userId',
      'root.approverComments.*.approverUserId'
    ]
  },
  add_data_subject_deletion_request: { ignored: [] },
  update_data_subject_deletion_request: { ignored: [] },
  post_decision_for_project: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.creationDate',
      'root.endDate',
      'root.lastUpdated',
      'root.startDate',
      'root.timeStamp',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',
      'root.approvers.*.primaryKey.userId',
      'root.approverComments.*.approverUserId'
    ]
  },
  post_decision_for_deletion_request: { ignored: [] },
  add_archives: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.startDate',
      'root.creationDate',
      'root.lastUpdated',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',
      'root.projectSearchUsers.*.primaryKey.userId',
      'root.approvers.*.primaryKey.userId',
      'root.collaborators.*.primaryKey.userId',
      'root.approverComments.*.approverUserId'
    ]
  },

  // Discovery Annotaion
  add_comment: {
    ignored: ['root.id'],
    resolve_epoch: ['root.timeStamp', 'root.createdAt'],
    resolve: ['root.userId']
  },
  remove_comment: {
    ignored: ['root.id'],
    resolve_epoch: ['root.timeStamp', 'root.createdAt'],
    resolve: ['root.userId']
  },
  update_comment: {
    ignored: ['root.id'],
    resolve_epoch: ['root.timeStamp', 'root.createdAt'],
    resolve: ['root.userId']
  },
  delete_tag: {
    ignored: ['root.id'],
    resolve_epoch: ['root.timeStamp', 'root.createdAt'],
    resolve: ['root.userId']
  },
  add_tag: {
    ignored: ['root.id'],
    resolve_epoch: ['root.timeStamp', 'root.createdAt'],
    resolve: ['root.userId']
  },
  resolve_comment: {
    ignored: ['root.id'],
    resolve_epoch: ['root.createdAt'],
    resolve: ['root.userId']
  },

  // Organisation Settings
  add_availabilityAlertDailySubscriber: {
    ignored: [],
    resolve: [],
    noPreview: true
  },
  add_availabilityAlertHourlySubscriber: {
    ignored: [],
    resolve: [],
    noPreview: true
  },
  add_incomingIpOrDomainAddress: { ignored: [], resolve: [] },
  add_orgBlacklist: { ignored: [], resolve: [] },
  remove_orgBlacklist: { ignored: [], resolve: [], noPreview: true },
  remove_orgWhitelist: { ignored: [], resolve: [], noPreview: true },
  add_orgWhitelist: { ignored: [], resolve: [] },
  add_outgoingIpOrDomainAddress: { ignored: [], resolve: [] },
  remove_availabilityAlertDailySubscriber: {
    ignored: [],
    resolve: [],
    noPreview: true
  },
  remove_availabilityAlertHourlySubscriber: {
    ignored: [],
    resolve: [],
    noPreview: true
  },
  remove_incomingIpOrDomainAddress: {
    ignored: [],
    resolve: []
  },
  remove_outgoingIpOrDomainAddress: {
    ignored: [],
    resolve: []
  },
  update_availabilityAlertDaily: { ignored: [], resolve: [] },
  update_businessContinuityPermanentBouncePolicy: { ignored: [], resolve: [] },
  update_availabilityAlertHourly: { ignored: [], resolve: [] },
  update_businessContinuityContinueMode: { ignored: [], resolve: [] },
  update_businessContinuityTemporaryBouncePolicy: { ignored: [], resolve: [] },
  update_domainBlackListSettings: { ignored: [], resolve: [] },

  update_domainWhiteListSettings: { ignored: [], resolve: [] },
  update_discoveryToolMinimumApprover: { ignored: [], resolve: [] },
  update_emailFooter: { ignored: [], resolve: [] },
  update_mailboxSettings: { ignored: [], resolve: [] },
  update_spamNotification: { ignored: [], resolve: [] },
  update_virusIncomingSettings: { ignored: [], resolve: [] },
  update_virusOutgoingSettings: { ignored: [], resolve: [] },
  add_discoveryToolMinimumApprover: { ignored: [], resolve: [] },
  udpate_discoveryToolMinimumApprover: { ignored: [], resolve: [] },

  // Settings
  add_userWhitelist: { ignored: [], resolve: [], noPreview: true },
  add_userBlacklist: { ignored: [], resolve: [], noPreview: true },
  remove_userBlacklist: { ignored: [], resolve: [], noPreview: true },
  update_userTheme: { ignored: [], resolve: [] },
  remove_userWhitelist: { ignored: [], resolve: [], noPreview: true },
  add_availability_alert_daily_subscriber: { ignored: [], resolve: [] },
  add_availability_alert_hourly_subscriber: { ignored: [], resolve: [] },
  add_domains: { ignored: [], resolve: [], noPreview: true },
  add_incoming_ip_or_domain_address: { ignored: [], resolve: [] },
  add_org_blacklist: { ignored: [], resolve: [] },
  add_org_whitelist: { ignored: [], resolve: [] },
  add_outgoing_ip_or_domain_address: { ignored: [], resolve: [] },
  remove_availability_alert_daily_subscriber: { ignored: [], resolve: [] },
  remove_availability_alert_hourly_subscriber: { ignored: [], resolve: [] },
  remove_domains: { ignored: [], resolve: [], noPreview: true },
  remove_incoming_ip_or_domain_address: { ignored: [], resolve: [] },
  remove_org_blacklist: { ignored: [], resolve: [] },
  remove_org_whitelist: { ignored: [], resolve: [] },
  remove_outgoing_ip_or_domain_address: { ignored: [], resolve: [] },
  update_availability_alert_daily: { ignored: [], resolve: [] },
  update_availability_alert_hourly: { ignored: [], resolve: [] },
  update_business_continuity_continue_mode: { ignored: [], resolve: [] },
  update_business_continuity_permanent_bounce_policy: {
    ignored: [],
    resolve: []
  },
  update_business_continuity_temporary_bounce_policy: {
    ignored: [],
    resolve: []
  },
  update_domain_blacklist_settings: { ignored: [], resolve: [] },
  update_domain_whitelist_settings: { ignored: [], resolve: [] },
  update_email_footer: { ignored: [], resolve: [] },
  update_mailbox_settings: { ignored: [], resolve: [] },
  update_spam_notification: { ignored: [], resolve: [] },
  update_timezone: { ignored: [], resolve: [] },
  update_virus_incoming_settings: { ignored: [], resolve: [] },
  update_virus_outgoing_settings: { ignored: [], resolve: [] },

  //Archive Extraction
  archive_export: { ignored: [], resolve: [] },
  discovery_project_export: { ignored: [], resolve: [] },

  // Dashboard
  add_widget: { ignored: [], resolve: [] },
  update_widget: {
    ignored: ['root.id']
  },
  remove_widget: { ignored: [], resolve: [] },

  // Restoration
  archive_restore_requested: {
    ignored: [],
    resolve: [],
    resolveEpoch: [
      'root.updatedAt',
      'root.owners.*.createdAt',
      'root.owners.*.updatedAt',
      'root.createdAt'
    ],
    noPreview: true
  },

  // Vault On-Boarding
  inititate_onboard_job: { ignored: [], resolve: [] },
  reinitiate_onboard_job: { ignored: [], resolve: [] },

  // Syncronisation
  add_user: {
    ignored: ['id', 'dbVersion'],
    resolve: ['userId']
  },
  update_user: {
    resolve: ['root.userId'],
    ignored: ['root.id', 'root.dbVersion', 'root.userExternalIds.*.id']
  },
  generate_temporary_password: { ignored: [], resolve: [], noPreview: true },

  add_discovery_project: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.projectOrganisation',
      'root.approverComments.*.id'
    ],
    resolve: [
      'root.projectOwner',
      'root.projectSearchUsers.*.primaryKey.userId'
    ],
    resolve_epoch: [
      'root.creationDate',
      'root.endDate',
      'root.lastUpdated',
      'root.startDate'
    ]
  },
  update_organisation: {
    ignored: [
      'root.id',
      'root.orgId',
      'root.organisationAddress.id',
      'root.organisationBillingSetting.id',
      'root.organisationSyncData.id',
      'root.varStatus'
    ],
    resolve_epoch: [
      'root.organisationSyncData.lastSyncDate',
      'root.organisationExternalIds.*.creationDate',
      'root.organisationBillingSetting.billingStart'
    ],
    resolve: []
  },
  add_organisation: {
    ignored: [
      'root.id',
      'root.orgId',
      'root.organisationAddress.id',
      'root.organisationBillingSetting.id'
    ],
    resolve: []
  },
  create_role: { ignored: [], resolve: [] },
  create_permission: { ignored: [], resolve: [] },
  update_permission: { ignored: [], resolve: [] },

  // Vault Off-Boarding
  inititate_offboard_job: { ignored: [], resolve: [] },
  reinitiate_offboard_job: { ignored: [], resolve: [] },

  // Vault Listeners
  execute_subscription: { ignored: [], resolve: [] },
  add_source: { ignored: [], resolve: [] },
  vault_listener: { ignored: [], resolve: [], noPreview: true },
  add_subscriptions: { ignored: [], resolve: [] },
  renew_subscription: { ignored: [], resolve: [] },
  recreate_subscription: { ignored: [], resolve: [] },
  unsubscribe_source: { ignored: [], resolve: [] },
  update_archiveToolBoxSubscriptions: {
    ignored: [],
    resolve: []
  },
  add_archiveToolBoxSubscriptions: { ignored: [], resolve: [] },
  update_discovery_project: {
    ignored: [
      'root.projectOrganisation',
      'root.id',
      'root.dbVersion',
      'root.approverComments.*.id'
    ],
    resolve_epoch: [
      'root.startDate',
      'root.creationDate',
      'root.endDate',
      'root.lastUpdated',
      'root.approverComments.*.date',
      'root.approvers.*.decisionDate'
    ],
    resolve: [
      'root.projectOwner',

      'root.approvers.*.primaryKey.userId',
      'root.approverComments.*.approverUserId'
    ]
  },

  edit_comment: {
    ignored: []
  },
  update_role: {
    resolve: [],
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.userExternalIds.*.id',
      'root.orgId',
      'root.organisationAddress.*.id',
      'root.organisationBillingSetting.*.id'
    ]
  },

  // User & Org
  add_account: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.organisation',
      'root.userAddress.id'
    ],
    resolve: ['root.userId', 'root.userGroupMembers.*.userId'],
    resolve_epoch: ['root.createdDate', 'root.lastUpdateDate']
  },
  update_account: {
    ignored: [
      'root.id',
      'root.dbVersion',
      'root.organisation',
      'root.userAddress.id',
      'root.keycloakRef',
      'root.userExternalIds.*.id'
    ],
    resolve_epoch: [
      'root.createdDate',
      'root.lastUpdateDate',
      'root.userExternalIds.*.createdDate'
    ],
    resolve: ['root.userId']
  },
  add_accountWhiteList: { ignored: [], noPreview: true },
  add_accountBlackList: { ignored: [] },
  remove_accountBlackList: { ignored: [] },
  update_accountTheme: { ignored: [], noPreview: true },
  remove_accountWhiteList: { ignored: [], noPreview: true },
  //
  login: { noPreview: true },
  logout: { noPreview: true }
};
